export interface RegentsOpenQuestion {
  id: number;
  part: 'I' | 'II' | 'III' | 'IV';
  points: number;
  topic: string;
  standard: string;
  question: string;
  diagram?: string;
  hint?: string;
  sampleAnswer: string[];
  rubric: string[];
  commonMistakes?: string[];
}

export interface RegentsMCQuestion {
  id: number;
  part: 'I';
  points: number;
  topic: string;
  standard: string;
  question: string;
  options: string[];
  correctAnswer: number;
  solution: string[];
}

export type RegentsQuestion = RegentsMCQuestion | RegentsOpenQuestion;

// ─── PART I — 24 Multiple Choice (2 pts each = 48 pts) ───────────────────────
export const partI: RegentsMCQuestion[] = [
  {
    id: 1, part: 'I', points: 2,
    topic: 'Transformations', standard: 'G.CO.A.2',
    question: 'Which transformation preserves both distance and angle measure?',
    options: ['Dilation', 'Horizontal stretch', 'Rigid motion (isometry)', 'Shear'],
    correctAnswer: 2,
    solution: [
      'A rigid motion (isometry) preserves both distance and angle measure.',
      'Dilations preserve angle measure but NOT distance (unless scale factor = 1).',
      'Stretches and shears distort both distance and angles.',
      'Answer: Rigid motion (isometry)'
    ]
  },
  {
    id: 2, part: 'I', points: 2,
    topic: 'Triangle Congruence', standard: 'G.CO.B.8',
    question: 'Which set of conditions is NOT sufficient to prove two triangles congruent?',
    options: ['SSS', 'SAS', 'AAA', 'AAS'],
    correctAnswer: 2,
    solution: [
      'AAA (Angle-Angle-Angle) proves similarity, NOT congruence.',
      'Two triangles can have the same angles but different side lengths (similar but not congruent).',
      'SSS, SAS, and AAS are all valid congruence criteria.',
      'Answer: AAA'
    ]
  },
  {
    id: 3, part: 'I', points: 2,
    topic: 'Parallel Lines & Transversals', standard: 'G.CO.C.9',
    question: 'Lines l and m are parallel, cut by transversal t. Angle 1 and Angle 5 are corresponding angles. If Angle 1 = 72°, what is Angle 5?',
    options: ['108°', '72°', '18°', '36°'],
    correctAnswer: 1,
    solution: [
      'Corresponding angles formed by parallel lines and a transversal are congruent.',
      'Angle 5 = Angle 1 = 72°',
      'Answer: 72°'
    ]
  },
  {
    id: 4, part: 'I', points: 2,
    topic: 'Pythagorean Theorem', standard: 'G.SRT.B.4',
    question: 'A right triangle has legs 9 and 40. What is the length of the hypotenuse?',
    options: ['41', '49', '√1681', '39'],
    correctAnswer: 0,
    solution: [
      'c² = a² + b² = 9² + 40² = 81 + 1600 = 1681',
      'c = √1681 = 41',
      'Answer: 41'
    ]
  },
  {
    id: 5, part: 'I', points: 2,
    topic: 'Trigonometry', standard: 'G.SRT.C.8',
    question: 'In right triangle ABC with right angle at C, tan(A) = 3/4. If BC = 12, what is AC?',
    options: ['9', '16', '15', '20'],
    correctAnswer: 1,
    solution: [
      'tan(A) = opposite/adjacent = BC/AC = 3/4',
      '3/4 = 12/AC',
      'AC = 12 × 4/3 = 16',
      'Answer: 16'
    ]
  },
  {
    id: 6, part: 'I', points: 2,
    topic: 'Circle — Inscribed Angle', standard: 'G.C.A.2',
    question: 'An inscribed angle intercepts a semicircle (arc = 180°). What is the measure of the inscribed angle?',
    options: ['180°', '360°', '90°', '45°'],
    correctAnswer: 2,
    solution: [
      'Inscribed Angle Theorem: inscribed angle = (1/2) × intercepted arc',
      'Inscribed angle = (1/2)(180°) = 90°',
      'This is Thales\' Theorem: an angle inscribed in a semicircle is always 90°.',
      'Answer: 90°'
    ]
  },
  {
    id: 7, part: 'I', points: 2,
    topic: 'Volume', standard: 'G.GMD.A.3',
    question: 'A sphere has radius 3. What is its volume?',
    options: ['12π', '36π', '9π', '108π'],
    correctAnswer: 1,
    solution: [
      'Volume of a sphere = (4/3)πr³',
      'V = (4/3)π(3)³ = (4/3)π(27) = 36π',
      'Answer: 36π'
    ]
  },
  {
    id: 8, part: 'I', points: 2,
    topic: 'Coordinate Geometry', standard: 'G.GPE.B.5',
    question: 'What is the slope of a line perpendicular to y = (2/3)x + 5?',
    options: ['2/3', '−3/2', '3/2', '−2/3'],
    correctAnswer: 1,
    solution: [
      'The slope of y = (2/3)x + 5 is 2/3.',
      'Perpendicular slopes are negative reciprocals: m⊥ = −3/2',
      'Answer: −3/2'
    ]
  },
  {
    id: 9, part: 'I', points: 2,
    topic: 'Dilation', standard: 'G.SRT.A.1',
    question: 'Triangle ABC is dilated by scale factor 3 centered at the origin. If A = (2, 4), what is A\'?',
    options: ['(6, 12)', '(5, 7)', '(2/3, 4/3)', '(6, 4)'],
    correctAnswer: 0,
    solution: [
      'Dilation centered at origin: (x, y) → (kx, ky)',
      'A\' = (3×2, 3×4) = (6, 12)',
      'Answer: (6, 12)'
    ]
  },
  {
    id: 10, part: 'I', points: 2,
    topic: 'Angle Sum — Triangle', standard: 'G.CO.C.10',
    question: 'In triangle PQR, angle P = 55° and angle Q = 78°. What is angle R?',
    options: ['47°', '133°', '57°', '67°'],
    correctAnswer: 0,
    solution: [
      'Sum of angles in a triangle = 180°',
      'Angle R = 180° − 55° − 78° = 47°',
      'Answer: 47°'
    ]
  },
  {
    id: 11, part: 'I', points: 2,
    topic: 'Equation of a Circle', standard: 'G.GPE.A.1',
    question: 'Which equation represents a circle with center (−3, 4) and radius 6?',
    options: [
      '(x+3)² + (y−4)² = 36',
      '(x−3)² + (y+4)² = 36',
      '(x+3)² + (y−4)² = 6',
      '(x−3)² + (y−4)² = 36'
    ],
    correctAnswer: 0,
    solution: [
      'Standard form: (x−h)² + (y−k)² = r²',
      'Center (h,k) = (−3, 4), r = 6',
      '(x−(−3))² + (y−4)² = 6² → (x+3)² + (y−4)² = 36',
      'Answer: (x+3)² + (y−4)² = 36'
    ]
  },
  {
    id: 12, part: 'I', points: 2,
    topic: 'Similarity Ratio', standard: 'G.SRT.A.2',
    question: 'Two similar triangles have areas 16 and 100. What is the ratio of their corresponding sides?',
    options: ['4:10', '2:5', '4:25', '16:100'],
    correctAnswer: 1,
    solution: [
      'The ratio of areas of similar figures = (ratio of sides)²',
      '16/100 = (side ratio)²',
      'Side ratio = √(16/100) = 4/10 = 2/5',
      'Answer: 2:5'
    ]
  },
  {
    id: 13, part: 'I', points: 2,
    topic: 'Rotation', standard: 'G.CO.A.4',
    question: 'Point A(3, 5) is rotated 90° counterclockwise about the origin. What are the coordinates of A\'?',
    options: ['(5, −3)', '(−5, 3)', '(−3, −5)', '(3, −5)'],
    correctAnswer: 1,
    solution: [
      'Rule for 90° CCW rotation about origin: (x, y) → (−y, x)',
      'A(3, 5) → A\'(−5, 3)',
      'Answer: (−5, 3)'
    ]
  },
  {
    id: 14, part: 'I', points: 2,
    topic: 'Midsegment Theorem', standard: 'G.CO.C.10',
    question: 'The midsegment of a triangle connects the midpoints of two sides. If the base of the triangle is 18, what is the length of the midsegment?',
    options: ['18', '36', '9', '6'],
    correctAnswer: 2,
    solution: [
      'Midsegment Theorem: the midsegment is parallel to the base and half its length.',
      'Midsegment = 18/2 = 9',
      'Answer: 9'
    ]
  },
  {
    id: 15, part: 'I', points: 2,
    topic: 'Tangent-Radius', standard: 'G.C.A.2',
    question: 'A tangent segment from external point P to circle O has length 8. The radius is 6. What is the distance from P to the center O?',
    options: ['14', '10', '2', '√28'],
    correctAnswer: 1,
    solution: [
      'The tangent is perpendicular to the radius at the point of tangency.',
      'This forms a right triangle: tangent² + radius² = PO²',
      '8² + 6² = PO²',
      '64 + 36 = 100',
      'PO = √100 = 10',
      'Answer: 10'
    ]
  },
  {
    id: 16, part: 'I', points: 2,
    topic: 'Special Right Triangles', standard: 'G.SRT.C.8',
    question: 'In a 30-60-90 triangle, the shorter leg is 7. What is the hypotenuse?',
    options: ['7√3', '14', '7√2', '21'],
    correctAnswer: 1,
    solution: [
      'In a 30-60-90 triangle: sides are in ratio 1 : √3 : 2',
      'Shorter leg (opposite 30°) = 7',
      'Hypotenuse = 2 × shorter leg = 2 × 7 = 14',
      'Answer: 14'
    ]
  },
  {
    id: 17, part: 'I', points: 2,
    topic: 'Polygon Interior Angles', standard: 'G.CO.C.10',
    question: 'What is the measure of each interior angle of a regular octagon?',
    options: ['120°', '135°', '140°', '150°'],
    correctAnswer: 1,
    solution: [
      'Sum of interior angles = (n−2) × 180° = (8−2) × 180° = 1080°',
      'Each interior angle = 1080° ÷ 8 = 135°',
      'Answer: 135°'
    ]
  },
  {
    id: 18, part: 'I', points: 2,
    topic: 'Coordinate Proof', standard: 'G.GPE.B.4',
    question: 'Quadrilateral ABCD has vertices A(0,0), B(6,0), C(8,4), D(2,4). What type of quadrilateral is it?',
    options: ['Rectangle', 'Rhombus', 'Trapezoid', 'Parallelogram'],
    correctAnswer: 3,
    solution: [
      'AB: slope = 0 (horizontal). DC: slope = (4−4)/(8−2) = 0 (horizontal). AB ∥ DC.',
      'AD: slope = (4−0)/(2−0) = 2. BC: slope = (4−0)/(8−6) = 2. AD ∥ BC.',
      'Both pairs of opposite sides are parallel → Parallelogram.',
      'AB = 6, AD = √(4+16) = √20 ≠ 6, so not a rhombus.',
      'Answer: Parallelogram'
    ]
  },
  {
    id: 19, part: 'I', points: 2,
    topic: 'Arc & Central Angle', standard: 'G.C.B.5',
    question: 'In circle O, central angle AOB = 150°. What is the measure of the major arc AB?',
    options: ['150°', '30°', '210°', '300°'],
    correctAnswer: 2,
    solution: [
      'The minor arc AB = central angle = 150°.',
      'Major arc AB = 360° − 150° = 210°',
      'Answer: 210°'
    ]
  },
  {
    id: 20, part: 'I', points: 2,
    topic: 'Surface Area', standard: 'G.GMD.A.3',
    question: 'What is the total surface area of a rectangular prism with length 5, width 4, and height 3?',
    options: ['60', '94', '47', '120'],
    correctAnswer: 1,
    solution: [
      'SA = 2(lw + lh + wh)',
      'SA = 2(5×4 + 5×3 + 4×3)',
      'SA = 2(20 + 15 + 12)',
      'SA = 2(47) = 94',
      'Answer: 94'
    ]
  },
  {
    id: 21, part: 'I', points: 2,
    topic: 'Geometric Mean', standard: 'G.SRT.B.5',
    question: 'The altitude drawn to the hypotenuse of a right triangle creates two smaller triangles. If the two segments of the hypotenuse are 4 and 9, what is the length of the altitude?',
    options: ['6', '13', '√13', '36'],
    correctAnswer: 0,
    solution: [
      'Geometric Mean (Altitude) Theorem: altitude² = product of the two hypotenuse segments',
      'altitude² = 4 × 9 = 36',
      'altitude = √36 = 6',
      'Answer: 6'
    ]
  },
  {
    id: 22, part: 'I', points: 2,
    topic: 'Transformations — Composition', standard: 'G.CO.A.5',
    question: 'A figure is reflected over the x-axis and then reflected over the y-axis. This composition is equivalent to which single transformation?',
    options: ['Translation', 'Rotation of 90°', 'Rotation of 180° about the origin', 'Dilation'],
    correctAnswer: 2,
    solution: [
      'Reflecting over x-axis: (x,y) → (x,−y)',
      'Then reflecting over y-axis: (x,−y) → (−x,−y)',
      'Net result: (x,y) → (−x,−y), which is a 180° rotation about the origin.',
      'Answer: Rotation of 180° about the origin'
    ]
  },
  {
    id: 23, part: 'I', points: 2,
    topic: 'Cavalieri\'s Principle', standard: 'G.GMD.A.2',
    question: 'Two solids have the same height and equal cross-sectional areas at every level. What can be concluded?',
    options: ['They are congruent', 'They have equal volumes', 'They have equal surface areas', 'They are similar'],
    correctAnswer: 1,
    solution: [
      'Cavalieri\'s Principle: if two solids have the same height and equal cross-sectional areas at every corresponding level, they have equal volumes.',
      'This does NOT mean they are congruent or have equal surface areas.',
      'Answer: They have equal volumes'
    ]
  },
  {
    id: 24, part: 'I', points: 2,
    topic: 'Locus', standard: 'G.GPE.B.4',
    question: 'What is the locus of points equidistant from two parallel lines?',
    options: ['A point', 'A circle', 'A line parallel to both, midway between them', 'Two lines'],
    correctAnswer: 2,
    solution: [
      'The locus of points equidistant from two parallel lines is a single line parallel to both, located exactly halfway between them.',
      'Answer: A line parallel to both, midway between them'
    ]
  }
];

// ─── PART II — 7 Open-Ended (2 pts each = 14 pts) ────────────────────────────
export const partII: RegentsOpenQuestion[] = [
  {
    id: 25, part: 'II', points: 2,
    topic: 'Transformations', standard: 'G.CO.A.5',
    question: 'Triangle ABC has vertices A(1, 2), B(4, 2), and C(4, 6). Graph and state the coordinates of triangle A\'B\'C\', the image of triangle ABC after a reflection over the y-axis.',
    diagram: `
DIAGRAM — Coordinate Plane:
      y
   6  |        C(4,6)
   5  |
   4  |
   3  |
   2  |  A(1,2)──B(4,2)
   1  |          |
   0  +──────────+──── x
      0  1  2  3  4  5
`,
    hint: 'Reflection over the y-axis rule: (x, y) → (−x, y)',
    sampleAnswer: [
      'Apply the reflection rule over the y-axis: (x, y) → (−x, y)',
      'A(1, 2) → A\'(−1, 2)',
      'B(4, 2) → B\'(−4, 2)',
      'C(4, 6) → C\'(−4, 6)',
      'The image triangle A\'B\'C\' has vertices at A\'(−1, 2), B\'(−4, 2), C\'(−4, 6).',
      'The triangle is flipped to the left side of the y-axis, maintaining the same shape and size.'
    ],
    rubric: [
      '2 points: All three image coordinates correct AND graph correctly drawn.',
      '1 point: At least two image coordinates correct, or correct rule applied with minor arithmetic error.',
      '0 points: Incorrect rule used or no work shown.'
    ],
    commonMistakes: [
      'Negating the y-coordinate instead of the x-coordinate.',
      'Forgetting to negate — writing the same coordinates.',
      'Reflecting over the x-axis instead of the y-axis.'
    ]
  },
  {
    id: 26, part: 'II', points: 2,
    topic: 'Pythagorean Theorem', standard: 'G.SRT.B.4',
    question: 'A ladder 13 feet long leans against a vertical wall. The base of the ladder is 5 feet from the wall. How high up the wall does the ladder reach? Show all work.',
    diagram: `
DIAGRAM — Right Triangle:
    |\\
    |  \\  13 ft (ladder)
  h |    \\
    |      \\
    +────────+
       5 ft
`,
    hint: 'The wall, ground, and ladder form a right triangle. Use a² + b² = c².',
    sampleAnswer: [
      'Identify the right triangle: legs are h (height on wall) and 5 ft (base); hypotenuse is 13 ft.',
      'Apply the Pythagorean Theorem: a² + b² = c²',
      'h² + 5² = 13²',
      'h² + 25 = 169',
      'h² = 169 − 25 = 144',
      'h = √144 = 12',
      'The ladder reaches 12 feet up the wall.'
    ],
    rubric: [
      '2 points: Correct setup, correct computation, answer 12 ft with work shown.',
      '1 point: Correct equation set up but arithmetic error in final answer.',
      '0 points: Wrong formula or no work shown.'
    ],
    commonMistakes: [
      'Adding 5 + 13 instead of using the Pythagorean Theorem.',
      'Forgetting to take the square root at the end.',
      'Treating 13 as a leg instead of the hypotenuse.'
    ]
  },
  {
    id: 27, part: 'II', points: 2,
    topic: 'Angle Relationships', standard: 'G.CO.C.9',
    question: 'In the diagram, lines AB and CD are parallel, cut by transversal EF. Angle AEF = (4x + 10)° and angle CFE = (6x − 20)°. Find the value of x and the measure of each angle. Justify your answer.',
    diagram: `
DIAGRAM — Parallel Lines:
    A ────────E──────── B
              |  (4x+10)°
              |
              |  (6x−20)°
    C ────────F──────── D
`,
    hint: 'Identify the relationship between the two angles (alternate interior, co-interior, or corresponding).',
    sampleAnswer: [
      'Angle AEF and angle CFE are alternate interior angles (between the parallel lines, on opposite sides of the transversal).',
      'Alternate interior angles are congruent when lines are parallel.',
      'Set the expressions equal: 4x + 10 = 6x − 20',
      'Subtract 4x from both sides: 10 = 2x − 20',
      'Add 20 to both sides: 30 = 2x',
      'x = 15',
      'Angle AEF = 4(15) + 10 = 60 + 10 = 70°',
      'Angle CFE = 6(15) − 20 = 90 − 20 = 70° ✓',
      'Both angles measure 70°.'
    ],
    rubric: [
      '2 points: Correct identification of angle relationship, correct equation, correct value of x, and correct angle measures.',
      '1 point: Correct relationship identified and equation set up, but arithmetic error in solving.',
      '0 points: Wrong relationship used or no justification.'
    ],
    commonMistakes: [
      'Treating the angles as supplementary (co-interior) instead of congruent (alternate interior).',
      'Setting up the equation incorrectly.',
      'Not verifying the answer by substituting back.'
    ]
  },
  {
    id: 28, part: 'II', points: 2,
    topic: 'Area & Perimeter', standard: 'G.GPE.B.7',
    question: 'On the coordinate plane, triangle DEF has vertices D(0, 0), E(6, 0), and F(3, 4). Find the area of triangle DEF.',
    diagram: `
DIAGRAM — Coordinate Triangle:
    y
  4 |      F(3,4)
  3 |     / \\
  2 |    /   \\
  1 |   /     \\
  0 +──D(0,0)──E(6,0)── x
    0  1  2  3  4  5  6
`,
    hint: 'Use the formula: Area = (1/2) × base × height. The base DE lies on the x-axis.',
    sampleAnswer: [
      'Identify the base: DE lies along the x-axis from (0,0) to (6,0).',
      'Base = 6 units.',
      'The height is the perpendicular distance from F to the base (the x-axis).',
      'F = (3, 4), so the height = 4 units (the y-coordinate of F).',
      'Area = (1/2) × base × height',
      'Area = (1/2) × 6 × 4 = 12 square units.'
    ],
    rubric: [
      '2 points: Correct base (6), correct height (4), correct area (12 sq units).',
      '1 point: Correct formula and setup, but arithmetic error.',
      '0 points: Wrong formula or no work shown.'
    ],
    commonMistakes: [
      'Using the wrong height (measuring diagonally instead of perpendicularly).',
      'Forgetting the (1/2) factor in the area formula.',
      'Confusing base and height.'
    ]
  },
  {
    id: 29, part: 'II', points: 2,
    topic: 'Circle — Tangent Segments', standard: 'G.C.A.2',
    question: 'From external point P, two tangent segments PA and PB are drawn to circle O. PA = 3x + 5 and PB = 5x − 7. Find the length of each tangent segment.',
    diagram: `
DIAGRAM — External Tangents:
         A
        /|
       / |  (radius)
      /  O
     \\  |
      \\ |  (radius)
       \\|
    P───B
  (external point)
`,
    hint: 'Two tangent segments drawn from the same external point to a circle are congruent.',
    sampleAnswer: [
      'Theorem: Two tangent segments from the same external point are congruent.',
      'Therefore PA = PB.',
      'Set up the equation: 3x + 5 = 5x − 7',
      'Subtract 3x: 5 = 2x − 7',
      'Add 7: 12 = 2x',
      'x = 6',
      'PA = 3(6) + 5 = 18 + 5 = 23',
      'PB = 5(6) − 7 = 30 − 7 = 23 ✓',
      'Each tangent segment has length 23.'
    ],
    rubric: [
      '2 points: Correct theorem stated, correct equation, correct value of x, correct length 23.',
      '1 point: Correct theorem and equation, but arithmetic error.',
      '0 points: Theorem not applied or wrong equation.'
    ],
    commonMistakes: [
      'Not knowing the equal tangent segments theorem.',
      'Setting up PA + PB = 180° (confusing with supplementary angles).',
      'Arithmetic errors when solving the linear equation.'
    ]
  },
  {
    id: 30, part: 'II', points: 2,
    topic: 'Volume', standard: 'G.GMD.A.3',
    question: 'A cylindrical water tank has a diameter of 10 feet and a height of 6 feet. How many cubic feet of water can it hold? Leave your answer in terms of π.',
    diagram: `
DIAGRAM — Cylinder:
    ┌─────────────┐
    │  diameter   │
    │  = 10 ft    │
    │             │ height
    │             │ = 6 ft
    │             │
    └─────────────┘
`,
    hint: 'Volume of a cylinder = πr²h. Remember: radius = diameter ÷ 2.',
    sampleAnswer: [
      'Diameter = 10 ft, so radius r = 10/2 = 5 ft.',
      'Height h = 6 ft.',
      'Volume of a cylinder = πr²h',
      'V = π(5)²(6)',
      'V = π(25)(6)',
      'V = 150π cubic feet.',
      'The tank can hold 150π cubic feet of water.'
    ],
    rubric: [
      '2 points: Correct radius (5), correct formula, correct answer 150π ft³.',
      '1 point: Used diameter instead of radius but set up formula correctly, or correct setup with arithmetic error.',
      '0 points: Wrong formula or no work shown.'
    ],
    commonMistakes: [
      'Using diameter (10) instead of radius (5) in the formula.',
      'Forgetting to square the radius.',
      'Computing a decimal approximation when the problem says "leave in terms of π".'
    ]
  },
  {
    id: 31, part: 'II', points: 2,
    topic: 'Midpoint & Distance', standard: 'G.GPE.B.6',
    question: 'Segment AB has endpoints A(−4, 1) and B(6, 9). Find the coordinates of the midpoint M of AB, and find the length of AB. Leave the length in simplest radical form.',
    hint: 'Use the midpoint formula and the distance formula.',
    sampleAnswer: [
      'Midpoint formula: M = ((x₁+x₂)/2, (y₁+y₂)/2)',
      'M = ((−4+6)/2, (1+9)/2) = (2/2, 10/2) = (1, 5)',
      'Midpoint M = (1, 5)',
      '',
      'Distance formula: d = √[(x₂−x₁)² + (y₂−y₁)²]',
      'd = √[(6−(−4))² + (9−1)²]',
      'd = √[(10)² + (8)²]',
      'd = √[100 + 64]',
      'd = √164 = √(4 × 41) = 2√41',
      'Length of AB = 2√41'
    ],
    rubric: [
      '2 points: Correct midpoint (1, 5) AND correct length 2√41.',
      '1 point: One of the two parts correct.',
      '0 points: Both parts incorrect or no work shown.'
    ],
    commonMistakes: [
      'Subtracting coordinates in the midpoint formula instead of adding.',
      'Not simplifying the radical (leaving √164 instead of 2√41).',
      'Forgetting to square the differences in the distance formula.'
    ]
  }
];

// ─── PART III — 3 Open-Ended (4 pts each = 12 pts) ───────────────────────────
export const partIII: RegentsOpenQuestion[] = [
  {
    id: 32, part: 'III', points: 4,
    topic: 'Triangle Congruence Proof', standard: 'G.CO.B.8',
    question: 'Given: AB ≅ DE, BC ≅ EF, and angle B ≅ angle E. Prove: Triangle ABC ≅ Triangle DEF.',
    diagram: `
DIAGRAM — Two Triangles:
    Triangle ABC:          Triangle DEF:
         A                      D
        / \\                    / \\
       /   \\                  /   \\
      B─────C                E─────F
    (angle B)              (angle E)

    Given: AB ≅ DE, BC ≅ EF, ∠B ≅ ∠E
`,
    hint: 'Identify which congruence postulate applies when two sides and the included angle are congruent.',
    sampleAnswer: [
      'PROOF (Two-Column Format):',
      '',
      'Statement 1: AB ≅ DE',
      'Reason 1: Given',
      '',
      'Statement 2: BC ≅ EF',
      'Reason 2: Given',
      '',
      'Statement 3: ∠B ≅ ∠E',
      'Reason 3: Given',
      '',
      'Statement 4: Triangle ABC ≅ Triangle DEF',
      'Reason 4: SAS (Side-Angle-Side) Congruence Postulate — two sides and the included angle of one triangle are congruent to two sides and the included angle of another triangle.',
      '',
      'Conclusion: Triangle ABC ≅ Triangle DEF by SAS. ∎'
    ],
    rubric: [
      '4 points: All three "Given" statements listed with correct reasons, correct identification of SAS, and clear conclusion.',
      '3 points: All given statements listed, SAS identified, but conclusion missing or minor error.',
      '2 points: Two of three given statements listed with reasons, and postulate identified.',
      '1 point: Some correct statements but postulate not identified or wrong postulate used.',
      '0 points: No valid proof structure.'
    ],
    commonMistakes: [
      'Using SSS instead of SAS (forgetting the angle is the INCLUDED angle).',
      'Not writing "Given" as the reason for the given statements.',
      'Listing the triangles in the wrong correspondence order.'
    ]
  },
  {
    id: 33, part: 'III', points: 4,
    topic: 'Similarity & Trigonometry', standard: 'G.SRT.C.8',
    question: 'A surveyor needs to find the width of a river. She stands at point A on one bank and sights a tree at point C directly across the river. She walks 80 meters along the bank to point B. From B, she measures angle ABC = 62°. Find the width of the river (AC) to the nearest meter.',
    diagram: `
DIAGRAM — River Width Problem:
    C (tree)
    |
    |  ← width of river (AC) = ?
    |
    A ──────────── B
         80 m
    (angle ABC = 62°)
    Right angle at A (AC ⊥ AB)
`,
    hint: 'The angle at A is 90° (AC is perpendicular to the bank AB). Use trigonometry with angle B.',
    sampleAnswer: [
      'Set up the right triangle: right angle at A, angle B = 62°, AB = 80 m.',
      'We need AC (opposite to angle B), and we know AB (adjacent to angle B).',
      'Use tangent: tan(B) = opposite/adjacent = AC/AB',
      'tan(62°) = AC/80',
      'AC = 80 × tan(62°)',
      'tan(62°) ≈ 1.8807',
      'AC ≈ 80 × 1.8807',
      'AC ≈ 150.46',
      'AC ≈ 150 meters (to the nearest meter).',
      'The width of the river is approximately 150 meters.'
    ],
    rubric: [
      '4 points: Correct diagram/setup, correct trig ratio (tan), correct computation, correct answer 150 m.',
      '3 points: Correct trig ratio and setup, minor rounding error in final answer.',
      '2 points: Correct trig ratio identified but set up incorrectly (e.g., inverted fraction).',
      '1 point: Some correct work but wrong trig function used.',
      '0 points: No valid approach.'
    ],
    commonMistakes: [
      'Using sin or cos instead of tan.',
      'Inverting the ratio (writing 80/AC instead of AC/80).',
      'Not rounding to the nearest meter as required.',
      'Using angle A instead of angle B.'
    ]
  },
  {
    id: 34, part: 'III', points: 4,
    topic: 'Coordinate Geometry — Quadrilateral Proof', standard: 'G.GPE.B.4',
    question: 'Quadrilateral ABCD has vertices A(1, 1), B(5, 1), C(6, 4), and D(2, 4). Using coordinate geometry, prove that ABCD is a parallelogram but NOT a rectangle.',
    diagram: `
DIAGRAM — Coordinate Quadrilateral:
    y
  4 |  D(2,4)────────C(6,4)
  3 |   /              /
  2 |  /              /
  1 | A(1,1)────────B(5,1)
  0 +──────────────────── x
    0  1  2  3  4  5  6
`,
    hint: 'For a parallelogram: show both pairs of opposite sides are parallel (equal slopes). For NOT a rectangle: show adjacent sides are NOT perpendicular (slopes are not negative reciprocals).',
    sampleAnswer: [
      'STEP 1: Show ABCD is a parallelogram (both pairs of opposite sides parallel).',
      '',
      'Slope of AB = (1−1)/(5−1) = 0/4 = 0',
      'Slope of DC = (4−4)/(6−2) = 0/4 = 0',
      'AB ∥ DC (equal slopes) ✓',
      '',
      'Slope of AD = (4−1)/(2−1) = 3/1 = 3',
      'Slope of BC = (4−1)/(6−5) = 3/1 = 3',
      'AD ∥ BC (equal slopes) ✓',
      '',
      'Since both pairs of opposite sides are parallel, ABCD is a parallelogram.',
      '',
      'STEP 2: Show ABCD is NOT a rectangle (adjacent sides not perpendicular).',
      '',
      'For perpendicular lines: slopes must be negative reciprocals (m₁ × m₂ = −1).',
      'Slope of AB = 0, Slope of AD = 3',
      '0 × 3 = 0 ≠ −1',
      'AB is not perpendicular to AD.',
      '',
      'Therefore, ABCD is a parallelogram but NOT a rectangle. ∎'
    ],
    rubric: [
      '4 points: All four slopes correctly calculated, parallelogram proven with both pairs of parallel sides, and non-rectangle proven with perpendicularity check.',
      '3 points: Parallelogram proven correctly, but non-rectangle argument incomplete or missing.',
      '2 points: Two slopes correct and one pair of parallel sides shown.',
      '1 point: Some correct slope calculations but no valid conclusion.',
      '0 points: No valid coordinate geometry work.'
    ],
    commonMistakes: [
      'Only showing one pair of parallel sides (not sufficient for parallelogram).',
      'Forgetting to prove the "NOT a rectangle" part.',
      'Slope formula errors (subtracting in wrong order).',
      'Confusing parallel (equal slopes) with perpendicular (negative reciprocal slopes).'
    ]
  }
];

// ─── PART IV — 1 Extended Response (6 pts) ───────────────────────────────────
export const partIV: RegentsOpenQuestion[] = [
  {
    id: 35, part: 'IV', points: 6,
    topic: 'Geometric Modeling — Composite Solids', standard: 'G.MG.A.1',
    question: `A grain silo consists of a cylinder topped with a hemisphere (half-sphere). The cylinder has a radius of 8 feet and a height of 20 feet. The hemisphere sits on top of the cylinder and has the same radius of 8 feet.

(a) Find the total volume of the silo (cylinder + hemisphere). Leave your answer in terms of π.

(b) If grain fills the silo to 75% of its total volume, how many cubic feet of grain are in the silo? Round to the nearest cubic foot.

(c) If the silo is painted on the outside (lateral surface of cylinder + curved surface of hemisphere, NOT the base), what is the total painted surface area? Leave your answer in terms of π.`,
    diagram: `
DIAGRAM — Grain Silo:
         ╭─────╮
        ╱       ╲   ← Hemisphere (r = 8 ft)
       │         │
       │         │
       │         │  ← Cylinder (r = 8 ft, h = 20 ft)
       │         │
       │         │
       └─────────┘
         (base — not painted)
`,
    hint: 'Volume of cylinder = πr²h. Volume of sphere = (4/3)πr³, so hemisphere = (2/3)πr³. Lateral surface area of cylinder = 2πrh. Curved surface area of hemisphere = 2πr².',
    sampleAnswer: [
      '═══ PART (a): Total Volume ═══',
      '',
      'Volume of cylinder = πr²h = π(8)²(20) = π(64)(20) = 1280π ft³',
      '',
      'Volume of hemisphere = (2/3)πr³ = (2/3)π(8)³ = (2/3)π(512) = (1024/3)π ft³',
      '',
      'Total volume = 1280π + (1024/3)π',
      '= (3840/3)π + (1024/3)π',
      '= (4864/3)π ft³',
      '',
      'Total volume = (4864/3)π ft³  ✓',
      '',
      '═══ PART (b): 75% of Total Volume ═══',
      '',
      'Volume of grain = 0.75 × (4864/3)π',
      '= (0.75 × 4864/3)π',
      '= (3648/3)π',
      '= 1216π',
      '≈ 1216 × 3.14159',
      '≈ 3820.7',
      '≈ 3821 cubic feet',
      '',
      'The silo contains approximately 3,821 cubic feet of grain.',
      '',
      '═══ PART (c): Painted Surface Area ═══',
      '',
      'Lateral surface area of cylinder = 2πrh = 2π(8)(20) = 320π ft²',
      '',
      'Curved surface area of hemisphere = 2πr² = 2π(8)² = 2π(64) = 128π ft²',
      '',
      'Total painted area = 320π + 128π = 448π ft²',
      '',
      'Total painted surface area = 448π ft²  ✓'
    ],
    rubric: [
      '6 points: All three parts fully correct with work shown — correct formulas, correct substitution, correct answers.',
      '5 points: Two parts fully correct, third part has minor arithmetic error.',
      '4 points: Parts (a) and (c) correct, part (b) has error; OR all three parts set up correctly with one computational error each.',
      '3 points: Two of three parts correct.',
      '2 points: One part fully correct, or correct formulas used throughout with multiple arithmetic errors.',
      '1 point: Correct formulas identified but significant errors in all computations.',
      '0 points: No valid work or completely wrong approach.'
    ],
    commonMistakes: [
      'Using full sphere volume instead of hemisphere (forgetting to divide by 2).',
      'Using diameter (16) instead of radius (8) in formulas.',
      'Forgetting to convert 75% to 0.75 in part (b).',
      'Including the base circle in the surface area (problem says NOT the base).',
      'Not leaving answers in terms of π when instructed.',
      'Forgetting to add the cylinder and hemisphere volumes together.'
    ]
  }
];
